import { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Typography, Paper, IconButton, Stack, Container } from '@mui/material';
import { ArrowBackIosNewOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router';

import moment from 'moment';
import * as XLSX from 'xlsx';
import { employeeCreditLimitsApi } from 'utils/API/Mis Apis/HrPortelApis';
import { handleCatchError } from 'utils/CatchHandler';
import { globalNotifySuccess, globalNotifyError } from 'views/utilities/AlertToast';

// Subcomponents
import CreditLimitsToolbar from './components/credit-limits/CreditLimitsToolbar';
import CreditLimitsTable from './components/credit-limits/CreditLimitsTable';
import EditCreditLimitModal from './components/credit-limits/EditCreditLimitModal';
import SettleRepaymentModal from './components/credit-limits/SettleRepaymentModal';
import BulkUploadModal from './components/credit-limits/BulkUploadModal';
import CreditBalanceModal from './components/credit-limits/CreditBalanceModal';
import DebitBalanceModal from './components/credit-limits/DebitBalanceModal';
import BalanceAdjustmentsHistoryModal from './components/credit-limits/BalanceAdjustmentsHistoryModal';
import OnboardEmployeeModal from './components/credit-limits/OnboardEmployeeModal';
import ToggleEmployeeStatusModal from './components/credit-limits/ToggleEmployeeStatusModal';
import UploadOutstandingModal from './components/credit-limits/UploadOutstandingModal';

const CreditLimits = () => {
    const navigate = useNavigate();

    // ─── Table Data & Loading ─────────────────────────────────────────
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [isExporting, setIsExporting] = useState(false);

    // ─── Search & Filters ─────────────────────────────────────────────
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [typeFilter, setTypeFilter] = useState('All');

    // ─── Pagination ──────────────────────────────────────────────────
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // ─── Modals State ────────────────────────────────────────────────
    // Modal A: Edit / Set Credit Limit
    const [limitModalOpen, setLimitModalOpen] = useState(false);
    const [editingAccount, setEditingAccount] = useState(null);

    // Modal B: Settle Repayment
    const [settleModalOpen, setSettleModalOpen] = useState(false);
    const [settleAccount, setSettleAccount] = useState(null);

    // Modal C: Bulk Upload Adjustments
    const [uploadModalOpen, setUploadModalOpen] = useState(false);

    // Modal C2: Upload Credit Outstanding
    const [outstandingModalOpen, setOutstandingModalOpen] = useState(false);

    // Modal D: Credit Balance
    const [creditBalanceModalOpen, setCreditBalanceModalOpen] = useState(false);
    const [creditBalanceAccount, setCreditBalanceAccount] = useState(null);

    // Modal E: Debit Balance
    const [debitBalanceModalOpen, setDebitBalanceModalOpen] = useState(false);
    const [debitBalanceAccount, setDebitBalanceAccount] = useState(null);

    // Modal F: Balance Adjustments History
    const [historyModalOpen, setHistoryModalOpen] = useState(false);
    const [historyAccount, setHistoryAccount] = useState(null);

    // Modal G: Onboard Employee
    const [onboardModalOpen, setOnboardModalOpen] = useState(false);

    // Modal H: Deactivate / Activate Employee Credit Access
    const [statusModalOpen, setStatusModalOpen] = useState(false);
    const [statusAccount, setStatusAccount] = useState(null);
    const [targetStatus, setTargetStatus] = useState('inactive');

    // ─── Fetch Data using employeeCreditLimitsApi ────────────────────
    const fetchCreditLimits = useCallback(async () => {
        setLoading(true);
        try {
            const payload = {
                pageNumber: page + 1,
                length: rowsPerPage
            };

            if (debouncedSearch) {
                payload.search = debouncedSearch;
            }
            if (statusFilter !== 'All') {
                payload.status = statusFilter;
            }
            if (typeFilter !== 'All') {
                payload.user_type = typeFilter;
            }

            const response = await employeeCreditLimitsApi(payload);

            let list = [];
            let count = 0;
            const resData = response?.data;

            if (resData?.success || response?.status === 200) {
                if (Array.isArray(resData?.data)) {
                    list = resData.data;
                } else if (Array.isArray(resData?.data?.data)) {
                    list = resData.data.data;
                    count = resData.data.total;
                } else if (Array.isArray(resData?.data?.records)) {
                    list = resData.data.records;
                } else if (Array.isArray(resData?.data?.credit_limits)) {
                    list = resData.data.credit_limits;
                } else if (Array.isArray(resData?.records)) {
                    list = resData.records;
                } else if (Array.isArray(resData?.credit_limits)) {
                    list = resData.credit_limits;
                } else if (Array.isArray(resData)) {
                    list = resData;
                }

                count =
                    count ||
                    resData?.totalRecordsWithFilter ||
                    resData?.total_count ||
                    resData?.totalcount ||
                    resData?.totalRecords ||
                    resData?.count ||
                    resData?.total ||
                    resData?.data?.total ||
                    list.length;
            } else if (Array.isArray(resData?.data)) {
                list = resData.data;
                count = resData.data.length;
            } else if (Array.isArray(resData)) {
                list = resData;
                count = resData.length;
            }

            setAccounts(Array.isArray(list) ? list : []);
            setTotalCount(Number(count) || (Array.isArray(list) ? list.length : 0));
        } catch (error) {
            console.error('Failed to fetch credit limits:', error);
            setAccounts([]);
            setTotalCount(0);
            handleCatchError(error);
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, debouncedSearch, statusFilter, typeFilter]);

    useEffect(() => {
        fetchCreditLimits();
    }, [fetchCreditLimits]);

    // ─── Debounce Search (300ms) ─────────────────────────────────────
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setPage(0);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // ─── Filter & Search Logic (Client side fallback if needed) ───────
    const filteredAccounts = useMemo(() => {
        if (!Array.isArray(accounts)) return [];

        const search = String(debouncedSearch || '')
            .toLowerCase()
            .trim();
        const lowerStatusFilter = statusFilter.toLowerCase();
        const lowerTypeFilter = typeFilter.toLowerCase();

        return accounts.filter((acc) => {
            if (!acc) return false;
            const name = String(acc.employee_name || acc.name || '').toLowerCase();
            const email = String(acc.email || '').toLowerCase();
            const code = String(acc.employee_code ?? acc.employee_id ?? acc.id ?? '').toLowerCase();
            const status = String(acc.status || '').toLowerCase();
            const type = String(acc.user_type || acc.role || '').toLowerCase();

            const matchesSearch = !search || name.includes(search) || email.includes(search) || code.includes(search);

            const matchesStatus = statusFilter === 'All' || status === lowerStatusFilter;
            const matchesType = typeFilter === 'All' || type === lowerTypeFilter;

            return matchesSearch && matchesStatus && matchesType;
        });
    }, [accounts, debouncedSearch, statusFilter, typeFilter]);

    const hasActiveFilters = statusFilter !== 'All' || typeFilter !== 'All';

    const handleClearFilters = () => {
        setStatusFilter('All');
        setTypeFilter('All');
        setPage(0);
    };

    // ─── Export Whole Data to Excel ──────────────────────────────────
    const handleExport = async () => {
        if (isExporting) return;
        setIsExporting(true);
        try {
            const payload = {
                export: true
            };

            const searchVal = (searchTerm || debouncedSearch || '').trim();
            if (searchVal) {
                payload.search = searchVal;
            }
            if (statusFilter !== 'All') {
                payload.status = statusFilter;
            }
            if (typeFilter !== 'All') {
                payload.user_type = typeFilter;
            }

            const response = await employeeCreditLimitsApi(payload);

            // 1. Check if backend returns a direct file download link
            const resData = response?.data;
            const fileUrl =
                resData?.file_url ||
                resData?.url ||
                resData?.file ||
                resData?.download_url ||
                resData?.downloadUrl ||
                resData?.data?.file_url ||
                resData?.data?.url ||
                resData?.data?.file ||
                (typeof resData?.data === 'string' &&
                (resData.data.startsWith('http') || resData.data.endsWith('.xlsx') || resData.data.endsWith('.csv'))
                    ? resData.data
                    : null);

            if (fileUrl) {
                const link = document.createElement('a');
                link.href = fileUrl;
                link.setAttribute('download', `Credit_Limits_${moment().format('YYYYMMDD_HHmmss')}.xlsx`);
                link.target = '_blank';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                globalNotifySuccess('Credit limits exported successfully');
                return;
            }

            // 2. Extract array data from response
            let exportData = [];
            if (Array.isArray(resData?.data)) {
                exportData = resData.data;
            } else if (Array.isArray(resData?.credit_limits)) {
                exportData = resData.credit_limits;
            } else if (Array.isArray(resData?.accounts)) {
                exportData = resData.accounts;
            } else if (Array.isArray(resData)) {
                exportData = resData;
            } else if (accounts.length > 0) {
                exportData = accounts;
            }

            if (!exportData || exportData.length === 0) {
                globalNotifyError(resData?.message || 'No credit limit data available to export');
                return;
            }

            // 3. Format records for Excel export
            const hasKnownKeys = exportData.some(
                (r) => r.employee_name || r.name || r.email || r.monthly_limit !== undefined || r.credit_limit !== undefined
            );

            const formattedRows = hasKnownKeys
                ? exportData.map((row) => ({
                      'Employee Name': row.employee_name || row.name || row.email?.split('@')[0] || '',
                      Email: row.email || '',
                      Phone: row.phone || row.mobileno || '',
                      'Account Type': row.user_type || row.role || 'HR Employee',
                      'Monthly Limit': Number(row.monthly_limit ?? row.credit_limit ?? 0),
                      'Current Outstanding': Number(row.current_outstanding ?? row.outstanding ?? row.outstanding_amount ?? 0),
                      Balance: Number(row.crossbanking_balance ?? row.balance ?? 0),
                      Status: row.status || 'Active',
                      'Updated At':
                          row.updated_at && moment(row.updated_at).isValid()
                              ? moment(row.updated_at).format('YYYY-MM-DD HH:mm:ss')
                              : row.updated_at || ''
                  }))
                : exportData;

            const worksheet = XLSX.utils.json_to_sheet(formattedRows);
            worksheet['!cols'] = [
                { wch: 25 }, // Employee Name
                { wch: 30 }, // Email
                { wch: 16 }, // Phone
                { wch: 18 }, // Account Type
                { wch: 16 }, // Monthly Limit
                { wch: 20 }, // Current Outstanding
                { wch: 16 }, // Balance
                { wch: 14 }, // Status
                { wch: 22 } // Updated At
            ];

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Credit_Limits');
            XLSX.writeFile(workbook, `Credit_Limits_${moment().format('YYYYMMDD_HHmmss')}.xlsx`);
            globalNotifySuccess('Credit limits exported to Excel successfully');
        } catch (error) {
            console.error('Failed to export credit limits:', error);
            handleCatchError(error);
        } finally {
            setIsExporting(false);
        }
    };

    // ─── Modal Open Handlers ─────────────────────────────────────────
    const handleOpenSetLimit = (account = null) => {
        setEditingAccount(account);
        setLimitModalOpen(true);
    };

    const handleOpenSettleModal = (account) => {
        setSettleAccount(account);
        setSettleModalOpen(true);
    };

    const handleOpenCreditBalanceModal = (account) => {
        setCreditBalanceAccount(account);
        setCreditBalanceModalOpen(true);
    };

    const handleOpenDebitBalanceModal = (account) => {
        setDebitBalanceAccount(account);
        setDebitBalanceModalOpen(true);
    };

    const handleOpenHistoryModal = (account = null) => {
        setHistoryAccount(account);
        setHistoryModalOpen(true);
    };

    const handleOpenOnboardModal = () => {
        setOnboardModalOpen(true);
    };

    const handleOpenUploadModal = () => {
        setUploadModalOpen(true);
    };

    const handleOpenStatusModal = (account, status = null) => {
        setStatusAccount(account);
        const currentIsActive = String(account?.status || '').toLowerCase() === 'active';
        const nextStatus = status || (currentIsActive ? 'inactive' : 'active');
        setTargetStatus(nextStatus);
        setStatusModalOpen(true);
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', pb: 6 }}>
            {/* ── Page Header / Banner ── */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                    px: { xs: 2, md: 4 },
                    pt: 3,
                    pb: 8,
                    borderRadius: '10px'
                }}
            >
                <Container maxWidth="xl" disableGutters>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                        <IconButton
                            onClick={() => navigate('/HR/dashboard')}
                            sx={{
                                color: '#fff',
                                bgcolor: 'rgba(255,255,255,0.15)',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }
                            }}
                        >
                            <ArrowBackIosNewOutlined fontSize="small" />
                        </IconButton>
                        <Box>
                            <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.7)', letterSpacing: '0.12em' }}>
                                HR Dashboard
                            </Typography>
                            <Typography variant="h3" fontWeight={800} sx={{ color: '#fff', mt: -0.5 }}>
                                Credit Limits
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', mt: 0.5 }}>
                                Showing {filteredAccounts.length} of {totalCount || (Array.isArray(accounts) ? accounts.length : 0)}{' '}
                                accounts
                            </Typography>
                        </Box>
                    </Stack>
                </Container>
            </Box>

            {/* ── Table Paper & Controls Card ── */}
            <Container maxWidth="xl" sx={{ mt: -4 }}>
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                        overflow: 'hidden',
                        bgcolor: '#fff',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
                    }}
                >
                    {/* Toolbar Subcomponent */}
                    <CreditLimitsToolbar
                        filteredCount={filteredAccounts.length}
                        totalCount={totalCount || (Array.isArray(accounts) ? accounts.length : 0)}
                        hasActiveFilters={hasActiveFilters}
                        searchTerm={searchTerm}
                        onSearchChange={setSearchTerm}
                        statusFilter={statusFilter}
                        onStatusFilterChange={setStatusFilter}
                        typeFilter={typeFilter}
                        onTypeFilterChange={setTypeFilter}
                        onClearFilters={handleClearFilters}
                        onOpenUploadModal={handleOpenUploadModal}
                        onOpenOutstandingModal={() => setOutstandingModalOpen(true)}
                        onOpenHistoryModal={() => handleOpenHistoryModal(null)}
                        onOpenOnboardModal={handleOpenOnboardModal}
                        onNavigateTransactionHistory={() => navigate('/HR/transaction-history')}
                        onExport={handleExport}
                        isExporting={isExporting}
                    />

                    {/* Table Subcomponent */}
                    <CreditLimitsTable
                        loading={loading}
                        accounts={filteredAccounts}
                        totalCount={totalCount}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        onPageChange={setPage}
                        onRowsPerPageChange={(newPerPage) => {
                            setRowsPerPage(newPerPage);
                            setPage(0);
                        }}
                        debouncedSearch={debouncedSearch}
                        hasActiveFilters={hasActiveFilters}
                        onClearFilters={handleClearFilters}
                        onOpenSetLimit={handleOpenSetLimit}
                        onOpenSettleModal={handleOpenSettleModal}
                        onOpenCreditBalanceModal={handleOpenCreditBalanceModal}
                        onOpenDebitBalanceModal={handleOpenDebitBalanceModal}
                        onOpenHistoryModal={handleOpenHistoryModal}
                        onOpenOnboardModal={handleOpenOnboardModal}
                        onOpenStatusModal={handleOpenStatusModal}
                    />
                </Paper>
            </Container>

            {/* ── Modal Dialog Subcomponents ── */}
            {/* Modal A: Set / Edit Credit Limit */}
            <EditCreditLimitModal
                open={limitModalOpen}
                onClose={() => setLimitModalOpen(false)}
                editingAccount={editingAccount}
                onSuccess={fetchCreditLimits}
            />

            {/* Modal B: Settle Repayment */}
            <SettleRepaymentModal
                open={settleModalOpen}
                onClose={() => setSettleModalOpen(false)}
                account={settleAccount}
                onSuccess={fetchCreditLimits}
            />

            {/* Modal C: Bulk Upload Adjustments */}
            <BulkUploadModal open={uploadModalOpen} onClose={() => setUploadModalOpen(false)} onSuccess={fetchCreditLimits} />

            {/* Modal C2: Upload Credit Outstanding */}
            <UploadOutstandingModal
                open={outstandingModalOpen}
                onClose={() => setOutstandingModalOpen(false)}
                onSuccess={fetchCreditLimits}
            />

            {/* Modal D: Credit Balance */}
            <CreditBalanceModal
                open={creditBalanceModalOpen}
                onClose={() => setCreditBalanceModalOpen(false)}
                account={creditBalanceAccount}
                onSuccess={fetchCreditLimits}
            />

            {/* Modal E: Debit Balance */}
            <DebitBalanceModal
                open={debitBalanceModalOpen}
                onClose={() => setDebitBalanceModalOpen(false)}
                account={debitBalanceAccount}
                onSuccess={fetchCreditLimits}
            />

            {/* Modal F: Balance Adjustments History */}
            <BalanceAdjustmentsHistoryModal
                open={historyModalOpen}
                onClose={() => setHistoryModalOpen(false)}
                initialAccount={historyAccount}
            />

            {/* Modal G: Onboard Employee */}
            <OnboardEmployeeModal open={onboardModalOpen} onClose={() => setOnboardModalOpen(false)} onSuccess={fetchCreditLimits} />

            {/* Modal H: Deactivate / Activate Employee Credit Access */}
            <ToggleEmployeeStatusModal
                open={statusModalOpen}
                onClose={() => setStatusModalOpen(false)}
                account={statusAccount}
                targetStatus={targetStatus}
                onSuccess={fetchCreditLimits}
            />
        </Box>
    );
};

export default CreditLimits;
